import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Search,
  Ticket,
  DollarSign,
  Users,
  ScanLine,
  Award,
  Video,
  Megaphone,
  Mail,
  Tag,
  RefreshCcw,
  Star,
  FileText,
  MapPin,
  ClipboardList,
  Package,
  Settings,
  LayoutDashboard,
  ExternalLink,
  Copy,
  PlusCircle,
  Building2,
  Calendar,
  CreditCard,
  UserCheck,
  ChevronRight,
  ShieldCheck,
  TrendingUp,
  Download,
  Layers,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getEventPublicPath } from "@/lib/urls";

interface CommandActionItem {
  id: string;
  title: string;
  category:
    | "Ações Rápidas"
    | "Edição do Evento"
    | "Ingressos & Lotes"
    | "Vendas & Transações"
    | "Inscritos & Participantes"
    | "Portaria & Acesso"
    | "Conteúdo & Transmissão"
    | "Marketing & Divulgação"
    | "Relatórios & Finanças"
    | "Navegação Geral";
  icon: React.ElementType;
  keywords?: string[];
  actionLabel?: string;
  /** When true, this command only makes sense inside an event and will navigate to events list if no event context */
  requiresEvent?: boolean;
  perform: () => void;
}

export function ProducerCommandPalette() {
  const { t } = useTranslation(["producerCommon", "common"]);
  const [open, setOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  // Detect OS: Mac vs Windows/Linux
  const isMac = useMemo(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") return false;
    return /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform || navigator.userAgent);
  }, []);

  const shortcutLabel = isMac ? "⌘K" : "Ctrl+K";

  // Detect if we are inside an event context (supporting both /produtor/eventos and /producer/events)
  const matchProducerPanel = location.pathname.match(/^\/(?:producer|produtor)\/(?:events|eventos)\/([^/]+)/);
  const matchAdminPanel = location.pathname.match(/^\/admin\/eventos\/([^/]+)/);

  const rawEventId = matchProducerPanel?.[1] || matchAdminPanel?.[1];
  const eventId = (rawEventId && rawEventId !== "new" && rawEventId !== "novo") ? rawEventId : undefined;
  const isInsideEvent = !!eventId;

  const isAdminArea = location.pathname.includes("/admin/");

  const panelBasePath = isAdminArea
    ? `/admin/eventos/${eventId}/painel`
    : `/produtor/eventos/${eventId}/painel`;

  // Fetch event basic info if inside event
  const { data: event } = useQuery({
    queryKey: ["producer-command-palette-event", eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, title, slug, status, visibility, organization_id, organizations(slug)")
        .eq("id", eventId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!eventId,
    staleTime: 60_000,
  });

  // Fetch recent events list (used when not inside an event, to navigate to event list)
  const { data: recentEvents } = useQuery({
    queryKey: ["producer-command-palette-recent-events"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data, error } = await supabase
        .from("events")
        .select("id, title")
        .order("created_at", { ascending: false })
        .limit(5);
      if (error) return [];
      return data ?? [];
    },
    enabled: open && !isInsideEvent,
    staleTime: 30_000,
  });

  // Global hotkey listener (Ctrl+K or Cmd+K)
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleNavigate = useCallback(
    (url: string) => {
      setOpen(false);
      navigate(url);
    },
    [navigate]
  );

  const handleEditorStep = useCallback(
    (stepKey: string) => {
      setOpen(false);
      if (!location.pathname.startsWith(panelBasePath)) {
        navigate(`${panelBasePath}?editorStep=${stepKey}`);
      } else {
        const params = new URLSearchParams(searchParams);
        params.set("editorStep", stepKey);
        setSearchParams(params);
      }
    },
    [location.pathname, navigate, panelBasePath, searchParams, setSearchParams]
  );

  const handleCopyLink = useCallback(() => {
    if (!event?.slug) return;
    const isOrg = !!event.organization_id && !!(event.organizations as any)?.slug;
    const path = getEventPublicPath(event.slug, (event.organizations as any)?.slug, isOrg);
    const fullUrl = `${window.location.origin}${path}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedLink(true);
    toast({
      title: t("commandPalette.toast.linkCopied.title"),
      description: t("commandPalette.toast.linkCopied.description"),
    });
    setTimeout(() => setCopiedLink(false), 2500);
    setOpen(false);
  }, [event, toast, t]);

  const handleOpenPublicPage = useCallback(() => {
    if (!event?.slug) return;
    const isOrg = !!event.organization_id && !!(event.organizations as any)?.slug;
    const path = getEventPublicPath(event.slug, (event.organizations as any)?.slug, isOrg);
    window.open(path, "_blank");
    setOpen(false);
  }, [event]);

  /**
   * When a command requires an event but we're not inside one,
   * navigate to the events list so the user can pick one.
   */
  const handleRequiresEvent = useCallback(
    (tab: string) => {
      setOpen(false);
      toast({
        title: t("commandPalette.toast.selectEvent.title"),
        description: t("commandPalette.toast.selectEvent.description", { feature: tab }),
      });
      navigate(isAdminArea ? "/admin/eventos" : "/produtor/eventos");
    },
    [navigate, toast, isAdminArea, t]
  );

  /** Returns a perform function: if inside event, goes to tab; otherwise, asks to pick an event */
  const eventAction = useCallback(
    (tab: string, tabLabel: string) => {
      if (isInsideEvent) {
        return () => handleNavigate(`${panelBasePath}/${tab}`);
      }
      return () => handleRequiresEvent(tabLabel);
    },
    [isInsideEvent, handleNavigate, handleRequiresEvent, panelBasePath]
  );

  /** For editor steps: if inside event, sets step; otherwise, redirects to list */
  const editorStepAction = useCallback(
    (stepKey: string, stepLabel: string) => {
      if (isInsideEvent) {
        return () => handleEditorStep(stepKey);
      }
      return () => handleRequiresEvent(stepLabel);
    },
    [isInsideEvent, handleEditorStep, handleRequiresEvent]
  );

  // Command items — ALL categories always shown
  const commandItems = useMemo<CommandActionItem[]>(() => {
    const items: CommandActionItem[] = [];

    // 1. Ações Rápidas
    if (isInsideEvent && event?.slug) {
      items.push({
        id: "event-open-public",
        title: t("commandPalette.items.eventOpenPublic.title"),
        category: "Ações Rápidas",
        icon: ExternalLink,
        keywords: ["pagina", "comprador", "publica", "site", "preview", "link", "ver"],
        actionLabel: t("commandPalette.actions.open"),
        perform: handleOpenPublicPage,
      });

      items.push({
        id: "event-copy-link",
        title: t("commandPalette.items.eventCopyLink.title"),
        category: "Ações Rápidas",
        icon: Copy,
        keywords: ["copiar", "link", "url", "compartilhar", "divulgar", "whatsapp"],
        actionLabel: copiedLink ? t("commandPalette.actions.copied") : t("commandPalette.actions.copy"),
        perform: handleCopyLink,
      });
    } else {
      items.push({
        id: "event-open-public",
        title: t("commandPalette.items.eventOpenPublic.title"),
        category: "Ações Rápidas",
        icon: ExternalLink,
        keywords: ["pagina", "comprador", "publica", "site", "preview", "link", "ver"],
        actionLabel: t("commandPalette.actions.selectEvent"),
        perform: () => handleRequiresEvent(t("commandPalette.features.publicPage")),
      });
      items.push({
        id: "event-copy-link",
        title: t("commandPalette.items.eventCopyLink.title"),
        category: "Ações Rápidas",
        icon: Copy,
        keywords: ["copiar", "link", "url", "compartilhar", "divulgar", "whatsapp"],
        actionLabel: t("commandPalette.actions.selectEvent"),
        perform: () => handleRequiresEvent(t("commandPalette.features.eventLink")),
      });
    }

    // 2. Edição do Evento
    items.push({
      id: "edit-info",
      title: t("commandPalette.items.editInfo.title"),
      category: "Edição do Evento",
      icon: FileText,
      keywords: ["informacoes", "titulo", "nome", "foto", "imagem", "banner", "descricao", "url", "slug", "categoria", "editar"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.edit") : t("commandPalette.actions.selectEvent"),
      perform: editorStepAction("info", t("commandPalette.features.infoEditing")),
    });

    items.push({
      id: "edit-venue",
      title: t("commandPalette.items.editVenue.title"),
      category: "Edição do Evento",
      icon: MapPin,
      keywords: ["local", "endereco", "cidade", "estado", "mapa", "presencial", "online", "live", "transmissao", "localizacao"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.edit") : t("commandPalette.actions.selectEvent"),
      perform: editorStepAction("venue", t("commandPalette.features.venue")),
    });

    items.push({
      id: "edit-form",
      title: t("commandPalette.items.editForm.title"),
      category: "Edição do Evento",
      icon: ClipboardList,
      keywords: ["formulario", "perguntas", "checkout", "cpf", "telefone", "campos", "dados", "questionario", "inscricao"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.edit") : t("commandPalette.actions.selectEvent"),
      perform: editorStepAction("form", t("commandPalette.features.checkoutForm")),
    });

    items.push({
      id: "edit-products",
      title: t("commandPalette.items.editProducts.title"),
      category: "Edição do Evento",
      icon: Package,
      keywords: ["produtos", "catalogo", "upsell", "adicionais", "combo", "camiseta", "brinde", "merch", "loja"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.edit") : t("commandPalette.actions.selectEvent"),
      perform: editorStepAction("products", t("commandPalette.features.additionalProducts")),
    });

    items.push({
      id: "edit-settings",
      title: t("commandPalette.items.editSettings.title"),
      category: "Edição do Evento",
      icon: Settings,
      keywords: ["configuracoes", "privacidade", "taxas", "cancelamento", "reembolso", "visibilidade", "privado", "publico", "termos"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.edit") : t("commandPalette.actions.selectEvent"),
      perform: editorStepAction("settings", t("commandPalette.features.eventSettings")),
    });

    // 3. Ingressos & Lotes
    items.push({
      id: "tab-tickets",
      title: t("commandPalette.items.tabTickets.title"),
      category: "Ingressos & Lotes",
      icon: Ticket,
      keywords: ["ingressos", "lotes", "criar ingresso", "preco", "valor", "estoque", "disponibilidade", "gratuito", "pago", "meia"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.open") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("ingressos", t("commandPalette.features.tickets")),
    });

    items.push({
      id: "action-new-ticket",
      title: t("commandPalette.items.actionNewTicket.title"),
      category: "Ingressos & Lotes",
      icon: PlusCircle,
      keywords: ["criar ingresso", "novo lote", "adicionar ingresso", "ingresso pago", "ingresso gratis"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.create") : t("commandPalette.actions.selectEvent"),
      perform: isInsideEvent
        ? () => handleNavigate(`${panelBasePath}/ingressos?action=new`)
        : () => handleRequiresEvent(t("commandPalette.features.tickets")),
    });

    items.push({
      id: "tickets-categories",
      title: t("commandPalette.items.ticketsCategories.title"),
      category: "Ingressos & Lotes",
      icon: Layers,
      keywords: ["cortesia", "gratis", "ingresso gratuito", "vip", "convite"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.view") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("ingressos", t("commandPalette.features.freeTickets")),
    });

    // 4. Vendas & Transações
    items.push({
      id: "tab-orders",
      title: t("commandPalette.items.tabOrders.title"),
      category: "Vendas & Transações",
      icon: DollarSign,
      keywords: ["pedidos", "vendas", "transacoes", "compras", "pagamentos", "pix", "cartao", "extrato", "estorno", "comprovante"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.view") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("pedidos", t("commandPalette.features.orders")),
    });

    items.push({
      id: "tab-coupons",
      title: t("commandPalette.items.tabCoupons.title"),
      category: "Vendas & Transações",
      icon: Tag,
      keywords: ["cupons", "desconto", "voucher", "promocional", "codigo", "oferta", "descontos", "promocao"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.open") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("cupons", t("commandPalette.features.coupons")),
    });

    items.push({
      id: "tab-resales",
      title: t("commandPalette.items.tabResales.title"),
      category: "Vendas & Transações",
      icon: RefreshCcw,
      keywords: ["revenda", "transferencia", "titularidade", "troca", "passar ingresso"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.open") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("revenda", t("commandPalette.features.resale")),
    });

    // 5. Inscritos & Participantes
    items.push({
      id: "tab-participants",
      title: t("commandPalette.items.tabParticipants.title"),
      category: "Inscritos & Participantes",
      icon: Users,
      keywords: ["participantes", "compradores", "inscritos", "leads", "base", "exportar", "excel", "cpf", "email"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.view") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("participantes", t("commandPalette.features.participants")),
    });

    items.push({
      id: "export-participants",
      title: t("commandPalette.items.exportParticipants.title"),
      category: "Inscritos & Participantes",
      icon: Download,
      keywords: ["exportar", "excel", "csv", "download participantes", "baixar lista"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.export") : t("commandPalette.actions.selectEvent"),
      perform: isInsideEvent
        ? () => handleNavigate(`${panelBasePath}/participantes?export=true`)
        : () => handleRequiresEvent(t("commandPalette.features.participantsExport")),
    });

    items.push({
      id: "tab-guestlist",
      title: t("commandPalette.items.tabGuestlist.title"),
      category: "Inscritos & Participantes",
      icon: UserCheck,
      keywords: ["convidados", "lista vip", "cortesia", "vip", "convite", "cortesias", "emissao manual"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.open") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("lista-convidados", t("commandPalette.features.guestlist")),
    });

    // 6. Portaria & Acesso
    items.push({
      id: "tab-checkin",
      title: t("commandPalette.items.tabCheckin.title"),
      category: "Portaria & Acesso",
      icon: ScanLine,
      keywords: ["checkin", "portaria", "leitor", "qrcode", "validar", "entrada", "acesso", "scanner"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.open") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("checkin", t("commandPalette.features.checkin")),
    });

    items.push({
      id: "portaria-realtime",
      title: t("commandPalette.items.portariaRealtime.title"),
      category: "Portaria & Acesso",
      icon: TrendingUp,
      keywords: ["presenca", "tempo real", "entrada", "quantidade presente", "lotação"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.view") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("checkin", t("commandPalette.features.realtimeAttendance")),
    });

    // 7. Conteúdo & Transmissão
    items.push({
      id: "tab-certificates",
      title: t("commandPalette.items.tabCertificates.title"),
      category: "Conteúdo & Transmissão",
      icon: Award,
      keywords: ["certificados", "diploma", "horas", "comprovante de presenca", "declaracao", "emitir"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.open") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("certificados", t("commandPalette.features.certificates")),
    });

    items.push({
      id: "tab-streaming",
      title: t("commandPalette.items.tabStreaming.title"),
      category: "Conteúdo & Transmissão",
      icon: Video,
      keywords: ["transmissao", "online", "live", "zoom", "youtube", "vimeo", "link ao vivo", "sala virtual"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.open") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("transmissao", t("commandPalette.features.streaming")),
    });

    // 8. Marketing & Divulgação
    items.push({
      id: "tab-promoters",
      title: t("commandPalette.items.tabPromoters.title"),
      category: "Marketing & Divulgação",
      icon: Megaphone,
      keywords: ["promoters", "promotores", "afiliados", "links", "vendedores", "comissao", "divulgacao", "rastreamento"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.open") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("promotores", t("commandPalette.features.promoters")),
    });

    items.push({
      id: "tab-messages",
      title: t("commandPalette.items.tabMessages.title"),
      category: "Marketing & Divulgação",
      icon: Mail,
      keywords: ["mensagens", "email", "comunicados", "avisos", "disparo", "notificacao", "lembrete"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.open") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("mensagens", t("commandPalette.features.messages")),
    });

    items.push({
      id: "tab-reviews",
      title: t("commandPalette.items.tabReviews.title"),
      category: "Marketing & Divulgação",
      icon: Star,
      keywords: ["avaliacoes", "feedback", "notas", "estrelas", "comentarios", "opinioes"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.view") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("avaliacoes", t("commandPalette.features.reviews")),
    });

    // 9. Relatórios & Finanças
    items.push({
      id: "tab-dashboard",
      title: t("commandPalette.items.tabDashboard.title"),
      category: "Relatórios & Finanças",
      icon: LayoutDashboard,
      keywords: ["dashboard", "visao geral", "vendas", "grafico", "faturamento", "receita", "metricas"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.view") : t("commandPalette.actions.selectEvent"),
      perform: isInsideEvent
        ? () => handleNavigate(panelBasePath)
        : () => handleRequiresEvent(t("commandPalette.features.eventDashboard")),
    });

    items.push({
      id: "tab-financial",
      title: t("commandPalette.items.tabFinancial.title"),
      category: "Relatórios & Finanças",
      icon: CreditCard,
      keywords: ["financeiro", "saldo", "repasse", "saque", "comissao", "receita liquida", "taxas", "banco"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.open") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("financeiro", t("commandPalette.features.eventFinancial")),
    });

    items.push({
      id: "tab-team",
      title: t("commandPalette.items.tabTeam.title"),
      category: "Relatórios & Finanças",
      icon: ShieldCheck,
      keywords: ["equipe", "time", "membros", "coprodutor", "permissoes", "acesso", "colaboradores"],
      actionLabel: isInsideEvent ? t("commandPalette.actions.manage") : t("commandPalette.actions.selectEvent"),
      perform: eventAction("equipe", t("commandPalette.features.team")),
    });

    // 10. Navegação Geral
    items.push({
      id: "nav-create-event",
      title: t("commandPalette.items.navCreateEvent.title"),
      category: "Navegação Geral",
      icon: PlusCircle,
      keywords: ["criar", "novo evento", "cadastrar", "adicionar evento"],
      actionLabel: t("commandPalette.actions.create"),
      perform: () => handleNavigate(isAdminArea ? "/admin/eventos/novo" : "/produtor/eventos/novo"),
    });

    items.push({
      id: "nav-events-list",
      title: t("commandPalette.items.navEventsList.title"),
      category: "Navegação Geral",
      icon: Calendar,
      keywords: ["eventos", "meus eventos", "lista de eventos", "todos os eventos"],
      actionLabel: t("commandPalette.actions.goTo"),
      perform: () => handleNavigate(isAdminArea ? "/admin/eventos" : "/produtor/eventos"),
    });

    items.push({
      id: "nav-dashboard",
      title: t("commandPalette.items.navDashboard.title"),
      category: "Navegação Geral",
      icon: LayoutDashboard,
      keywords: ["dashboard", "home", "inicio", "produtor"],
      actionLabel: t("commandPalette.actions.goTo"),
      perform: () => handleNavigate(isAdminArea ? "/admin/relatorios" : "/produtor/dashboard"),
    });

    items.push({
      id: "nav-financial-global",
      title: t("commandPalette.items.navFinancialGlobal.title"),
      category: "Navegação Geral",
      icon: DollarSign,
      keywords: ["financeiro", "extrato", "repasses", "saques", "conta bancaria", "conciliacao"],
      actionLabel: t("commandPalette.actions.goTo"),
      perform: () => handleNavigate(isAdminArea ? "/admin/financeiro" : "/produtor/financeiro"),
    });

    items.push({
      id: "nav-org",
      title: t("commandPalette.items.navOrg.title"),
      category: "Navegação Geral",
      icon: Building2,
      keywords: ["organizacao", "equipe", "membros", "empresa", "produtora", "time"],
      actionLabel: t("commandPalette.actions.goTo"),
      perform: () => handleNavigate(isAdminArea ? "/admin/organizacoes" : "/produtor/organizacoes"),
    });

    items.push({
      id: "nav-settings",
      title: t("commandPalette.items.navSettings.title"),
      category: "Navegação Geral",
      icon: Settings,
      keywords: ["configuracoes", "conta", "perfil", "senha", "dados da conta"],
      actionLabel: t("commandPalette.actions.goTo"),
      perform: () => handleNavigate(isAdminArea ? "/admin/configuracoes" : "/produtor/configuracoes"),
    });

    return items;
  }, [
    t,
    isInsideEvent,
    isAdminArea,
    eventId,
    event,
    copiedLink,
    handleOpenPublicPage,
    handleCopyLink,
    handleEditorStep,
    handleNavigate,
    handleRequiresEvent,
    panelBasePath,
    eventAction,
    editorStepAction,
  ]);

  const categories = useMemo(() => {
    const groups: Record<string, CommandActionItem[]> = {};
    commandItems.forEach((item) => {
      if (!groups[item.category]) {
        groups[item.category] = [];
      }
      groups[item.category].push(item);
    });
    return groups;
  }, [commandItems]);

  // Display labels for internal category ids (ids stay in Portuguese for grouping only)
  const categoryLabels: Record<string, string> = {
    "Ações Rápidas": t("commandPalette.categories.quickActions"),
    "Edição do Evento": t("commandPalette.categories.eventEditing"),
    "Ingressos & Lotes": t("commandPalette.categories.ticketsAndBatches"),
    "Vendas & Transações": t("commandPalette.categories.salesAndTransactions"),
    "Inscritos & Participantes": t("commandPalette.categories.participants"),
    "Portaria & Acesso": t("commandPalette.categories.checkinAndAccess"),
    "Conteúdo & Transmissão": t("commandPalette.categories.contentAndStreaming"),
    "Marketing & Divulgação": t("commandPalette.categories.marketing"),
    "Relatórios & Finanças": t("commandPalette.categories.reportsAndFinance"),
    "Navegação Geral": t("commandPalette.categories.generalNavigation"),
  };

  return (
    <>
      {/* Header Search Trigger */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "hidden sm:flex group relative items-center justify-between gap-2.5",
          "h-9 px-3 rounded-xl",
          "bg-muted/30 hover:bg-muted/60 active:bg-muted/80",
          "border border-border/40 hover:border-border/80",
          "text-xs text-muted-foreground hover:text-foreground",
          "transition-all duration-150 ease-out shadow-xs",
          "w-36 sm:w-52 md:w-60 lg:w-64"
        )}
      >
        <div className="flex items-center gap-2 truncate">
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70 group-hover:text-foreground transition-colors" />
          <span className="truncate font-normal">
            {t("commandPalette.trigger.placeholder")}
          </span>
        </div>
        <kbd className="pointer-events-none hidden sm:inline-flex h-4.5 select-none items-center gap-0.5 rounded border border-border/50 bg-background/60 px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-80">
          {shortcutLabel}
        </kbd>
      </button>

      {/* Command Dialog Modal */}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder={t("commandPalette.dialog.searchPlaceholder")}
        />

        {/* Context hint when not inside an event */}
        {!isInsideEvent && (
          <div className="flex items-center gap-2 px-4 py-2 border-b border-border/40 bg-muted/20">
            <Zap className="h-3 w-3 text-amber-500 shrink-0" />
            <p className="text-[11px] text-muted-foreground">
              {t("commandPalette.dialog.eventHint")}{" "}
              {recentEvents && recentEvents.length > 0 && (
                <span className="font-medium text-foreground/70">
                  {t("commandPalette.dialog.recentEventsCount", { count: recentEvents.length })}
                </span>
              )}
            </p>
          </div>
        )}

        <CommandList className="max-h-[480px] min-h-[320px] overflow-y-auto custom-scrollbar p-3 space-y-3">
          <CommandEmpty className="py-12 text-center text-xs text-muted-foreground font-normal">
            {t("commandPalette.list.empty")}
          </CommandEmpty>

          {Object.entries(categories).map(([categoryName, items]) => (
            <CommandGroup
              key={categoryName}
              heading={categoryLabels[categoryName] ?? categoryName}
              className="px-1 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground/60 [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-widest"
            >
              {items.map((item) => {
                const IconComponent = item.icon;
                const isEventRequired = !isInsideEvent && item.actionLabel?.includes("→");
                return (
                  <CommandItem
                    key={item.id}
                    value={`${item.title} ${(item.keywords || []).join(" ")}`}
                    onSelect={item.perform}
                    className={cn(
                      "flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-xs cursor-pointer my-0.5",
                      "transition-colors duration-100 ease-out",
                      "data-[selected='true']:bg-neutral-100 dark:data-[selected='true']:bg-neutral-800/80",
                      "data-[selected='true']:text-neutral-900 dark:data-[selected='true']:text-neutral-100",
                      "group",
                      isEventRequired && "opacity-60 hover:opacity-100"
                    )}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <IconComponent className="h-4 w-4 shrink-0 text-neutral-400 dark:text-neutral-500 group-data-[selected='true']:text-neutral-900 dark:group-data-[selected='true']:text-neutral-100 transition-colors" />
                      <span className="font-medium text-neutral-700 dark:text-neutral-200 group-data-[selected='true']:text-neutral-900 dark:group-data-[selected='true']:text-neutral-100 truncate">
                        {item.title}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      {item.actionLabel && (
                        <span className="text-[11px] font-normal text-neutral-400 dark:text-neutral-500 group-data-[selected='true']:text-neutral-600 dark:group-data-[selected='true']:text-neutral-300 flex items-center gap-1">
                          {item.actionLabel}
                          {!isEventRequired && (
                            <ChevronRight className="h-3 w-3 opacity-0 group-data-[selected='true']:opacity-100 transition-opacity" />
                          )}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
